import React, { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Database, Download, RefreshCw, Trash2, HardDrive, Clock, CheckCircle2, AlertTriangle, Cloud, CloudUpload, RotateCcw } from "lucide-react";

export default function BackupManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/system/backups");
      setItems(data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runBackup = async () => {
    if (!confirm("سيتم إنشاء نسخة احتياطية جديدة الآن. متابعة؟")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/system/backup");
      if (data.status === "ok") toast.success(`تم إنشاء النسخة: ${(data.size_bytes/1024).toFixed(1)} KB`);
      else toast.error("فشل: " + (data.error || "خطأ غير معروف"));
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRunning(false); }
  };

  const doDownload = async (b) => {
    const token = localStorage.getItem("access_token");
    const url = `${API}/system/backups/${b.id}/download`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("فشل التنزيل");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = b.filename;
      a.click();
      toast.success("تم بدء التنزيل");
    } catch (e) { toast.error(e.message); }
  };

  const doDelete = async (b) => {
    if (!confirm(`حذف النسخة ${b.filename}؟ لا يمكن التراجع.`)) return;
    try {
      await api.delete(`/system/backups/${b.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doCloudUpload = async (b) => {
    try {
      toast.info("جاري الرفع للسحابة...");
      const { data } = await api.post(`/system/backups/${b.id}/upload-cloud`);
      toast.success(`تم الرفع (${(data.size/1024).toFixed(1)} KB)`);
      load();
    } catch (e) { toast.error(formatErr(e) || "فشل الرفع السحابي"); }
  };

  const doCloudDownload = async (b) => {
    try {
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/system/backups/${b.id}/download-cloud`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("فشل التنزيل السحابي");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = b.filename;
      a.click();
      toast.success("تم تنزيل النسخة السحابية");
    } catch (e) { toast.error(e.message); }
  };

  const doRestore = async (b) => {
    const w1 = prompt(`⚠️ خطر جداً! ستُستعاد قاعدة البيانات من:\n${b.filename}\n\nكل البيانات الحالية ستُحذف واستبدالها بمحتوى النسخة.\n\nاكتب "RESTORE" للتأكيد:`);
    if (w1 !== "RESTORE") { toast.info("تم الإلغاء"); return; }
    const w2 = confirm("تأكيد أخير: هل تفهم أن كل البيانات الحالية ستُستبدل؟");
    if (!w2) return;
    try {
      toast.info("جاري الاستعادة... قد يستغرق دقيقة");
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/system/backups/${b.id}/restore`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "X-Confirm-Restore": "YES", "Content-Type": "application/json" },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "فشلت الاستعادة");
      toast.success(`تمت الاستعادة من: ${data.restored_from}`);
      setTimeout(() => window.location.reload(), 2000);
    } catch (e) { toast.error(e.message); }
  };

  const fmtBytes = (b) => {
    if (!b) return "0";
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/(1024*1024)).toFixed(2)} MB`;
  };

  const successCount = items.filter(i => i.status === "ok").length;
  const failedCount = items.filter(i => i.status !== "ok").length;
  const totalBytes = items.reduce((s, i) => s + (i.size_bytes || 0), 0);
  const latest = items.find(i => i.status === "ok");

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><Database className="w-7 h-7 text-primary" /> النسخ الاحتياطي</h1>
          <p className="text-sm text-muted-foreground mt-1">إدارة النسخ الاحتياطية · نسخة تلقائية يومياً 2:00 صباحاً UTC · يُحتفظ بآخر 30 نسخة</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="backup-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button onClick={runBackup} disabled={running} className="gap-2" data-testid="backup-run"><Database className="w-4 h-4" /> {running ? "جاري..." : "إنشاء نسخة الآن"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="backup-stat-total">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">إجمالي النسخ</span><HardDrive className="w-4 h-4 text-primary" /></div>
          <div className="mt-2 text-2xl font-bold tabular-nums">{items.length}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">ناجحة</span><CheckCircle2 className="w-4 h-4 text-emerald-600" /></div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-emerald-600">{successCount}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">فاشلة</span><AlertTriangle className="w-4 h-4 text-destructive" /></div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-destructive">{failedCount}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">إجمالي الحجم</span><HardDrive className="w-4 h-4" /></div>
          <div className="mt-2 text-2xl font-bold tabular-nums">{fmtBytes(totalBytes)}</div>
        </Card>
      </div>

      {latest && (
        <Card className="p-5 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30" data-testid="backup-latest">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-emerald-800 dark:text-emerald-300">آخر نسخة ناجحة</div>
              <div className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">{latest.filename}</div>
              <div className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">{new Date(latest.created_at).toLocaleString("ar-EG")} · {fmtBytes(latest.size_bytes)} · {latest.kind === "auto" ? "تلقائية" : "يدوية"}</div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="p-4 border-b border-border font-bold">قائمة النسخ الاحتياطية</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>التاريخ</TableHead>
              <TableHead>الملف</TableHead>
              <TableHead>الحجم</TableHead>
              <TableHead>النوع</TableHead>
              <TableHead>السحابة</TableHead>
              <TableHead>الحالة</TableHead>
              <TableHead>إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد نسخ احتياطية بعد. اضغط "إنشاء نسخة الآن" للبدء.</TableCell></TableRow>}
            {items.map(b => (
              <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(b.created_at).toLocaleString("ar-EG")}</TableCell>
                <TableCell className="font-mono text-xs truncate max-w-xs" title={b.filename}>{b.filename}</TableCell>
                <TableCell className="tabular-nums">{fmtBytes(b.size_bytes)}</TableCell>
                <TableCell>
                  {b.kind === "auto"
                    ? <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary flex items-center gap-1 w-fit"><Clock className="w-3 h-3" /> تلقائية</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 w-fit">يدوية</span>}
                </TableCell>
                <TableCell>
                  {b.cloud_path
                    ? <span className="text-xs px-2 py-0.5 rounded bg-sky-50 text-sky-700 flex items-center gap-1 w-fit" title={b.cloud_uploaded_at ? new Date(b.cloud_uploaded_at).toLocaleString("ar-EG") : ""} data-testid={`backup-cloud-${b.id}`}><Cloud className="w-3 h-3" /> مرفوعة</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground w-fit">محلية فقط</span>}
                </TableCell>
                <TableCell>
                  {b.status === "ok"
                    ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 flex items-center gap-1 w-fit"><CheckCircle2 className="w-3 h-3" /> ناجحة</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive flex items-center gap-1 w-fit" title={b.error}><AlertTriangle className="w-3 h-3" /> فاشلة</span>}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {b.status === "ok" && !b.cloud_path && <Button variant="ghost" size="sm" onClick={() => doCloudUpload(b)} title="رفع للسحابة" data-testid={`backup-cloud-up-${b.id}`}><CloudUpload className="w-4 h-4 text-sky-600" /></Button>}
                    {b.cloud_path && <Button variant="ghost" size="sm" onClick={() => doCloudDownload(b)} title="تنزيل من السحابة" data-testid={`backup-cloud-dl-${b.id}`}><Cloud className="w-4 h-4 text-sky-600" /></Button>}
                    {b.status === "ok" && <Button variant="ghost" size="sm" onClick={() => doDownload(b)} title="تنزيل محلي" data-testid={`backup-dl-${b.id}`}><Download className="w-4 h-4" /></Button>}
                    {b.status === "ok" && <Button variant="ghost" size="sm" onClick={() => doRestore(b)} title="استعادة (خطر!)" data-testid={`backup-restore-${b.id}`}><RotateCcw className="w-4 h-4 text-amber-600" /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => doDelete(b)} title="حذف" data-testid={`backup-del-${b.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
